import React, { useMemo, useState } from 'react';
import type { FormInstance } from 'antd';
import { Card, Form, InputNumber, Select, Space, Tabs, Tooltip, Typography } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { asPreviewCss } from '../utils/bookDefaultTemplates';
import { SectionLabel, StyleFieldGroup } from './common/IconFormLabel';
import { LineStyleEditTabs } from './karmakanda/LineStyleEditCollapse';
import './karmakanda/StyleModalTabs.css';

type Props = {
  form: FormInstance;
  previewTitle?: string;
};

function buildLinePreviewCss(
  lineStyle: Record<string, unknown> | undefined,
  defaultTextColor?: string,
): React.CSSProperties {
  const css = asPreviewCss(lineStyle || {});
  const widthPercent = Number(lineStyle?.widthPercent);
  const span = lineStyle?.span;
  const displayRaw = lineStyle?.display;
  const display =
    displayRaw === 'block' || displayRaw === 'inline' || displayRaw === 'inline-block'
      ? displayRaw
      : 'block';
  let width: React.CSSProperties['width'] = '100%';
  if (widthPercent > 0) {
    width = `${widthPercent}%`;
  } else if (span === 12) {
    width = '50%';
  }
  return {
    ...css,
    color:
      css.color ??
      (typeof defaultTextColor === 'string' && defaultTextColor.trim() ? defaultTextColor.trim() : undefined),
    display,
    width,
    maxWidth: '100%',
    whiteSpace: 'pre-wrap' as const,
    lineHeight:
      Number(lineStyle?.lineHeight) > 0 ? Number(lineStyle?.lineHeight) : css.lineHeight ?? 1,
  };
}

const previewRowStyle = (active: boolean): React.CSSProperties =>
  active
    ? {
        border: '1px solid #91caff',
        borderRadius: 6,
        padding: '4px 6px',
        background: '#f0f7ff',
      }
    : { padding: '2px 0' };

export const BookDefaultStylesEditor: React.FC<Props> = ({ form, previewTitle }) => {
  const [activeStyleTab, setActiveStyleTab] = useState('page');

  const headerTextColor = Form.useWatch(['defaultHeaderStyle', 'color'], form);
  const headerBgColor = Form.useWatch(['defaultHeaderStyle', 'backgroundColor'], form);
  const headerBorderColor = Form.useWatch(['defaultHeaderStyle', 'borderColor'], form);

  const lineTextColor = Form.useWatch(['defaultLineStyle', 'color'], form);
  const lineBgColor = Form.useWatch(['defaultLineStyle', 'backgroundColor'], form);
  const lineBorderColor = Form.useWatch(['defaultLineStyle', 'borderColor'], form);

  const nestedLineTextColor = Form.useWatch(['defaultNestedLineStyle', 'color'], form);
  const nestedLineBgColor = Form.useWatch(['defaultNestedLineStyle', 'backgroundColor'], form);
  const nestedLineBorderColor = Form.useWatch(['defaultNestedLineStyle', 'borderColor'], form);

  const deepNestedLineTextColor = Form.useWatch(['defaultDeepNestedLineStyle', 'color'], form);
  const deepNestedLineBgColor = Form.useWatch(['defaultDeepNestedLineStyle', 'backgroundColor'], form);
  const deepNestedLineBorderColor = Form.useWatch(['defaultDeepNestedLineStyle', 'borderColor'], form);

  const linkTextColor = Form.useWatch(['defaultLinkStyle', 'color'], form);
  const linkBgColor = Form.useWatch(['defaultLinkStyle', 'backgroundColor'], form);
  const linkBorderColor = Form.useWatch(['defaultLinkStyle', 'borderColor'], form);

  const contentLineGapPx = Form.useWatch('contentLineGapPx', form);
  const pagePaddingPx = Form.useWatch('pagePaddingPx', form);
  const contentAlign = Form.useWatch('contentAlign', form);
  const defaultTextColor = Form.useWatch('defaultTextColor', form);
  const defaultBackgroundColor = Form.useWatch('defaultBackgroundColor', form);

  const headerStyle = Form.useWatch('defaultHeaderStyle', form);
  const lineStyle = Form.useWatch('defaultLineStyle', form);
  const nestedLineStyle = Form.useWatch('defaultNestedLineStyle', form);
  const deepNestedLineStyle = Form.useWatch('defaultDeepNestedLineStyle', form);
  const linkStyle = Form.useWatch('defaultLinkStyle', form);

  const previewFlowStyle = useMemo((): React.CSSProperties => {
    const gap = Math.max(0, Number(contentLineGapPx) || 0);
    const align =
      contentAlign === 'center' || contentAlign === 'right' || contentAlign === 'justify'
        ? contentAlign
        : 'left';
    const style: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      gap,
      lineHeight: 1,
      width: '100%',
    };
    if (align === 'center') {
      style.textAlign = 'center';
      style.alignItems = 'center';
    } else if (align === 'right') {
      style.textAlign = 'right';
      style.alignItems = 'flex-end';
    } else if (align === 'justify') {
      style.textAlign = 'justify';
      style.alignItems = 'stretch';
    } else {
      style.textAlign = 'left';
      style.alignItems = 'stretch';
    }
    return style;
  }, [contentLineGapPx, contentAlign]);

  const pageTab = (
    <div className="style-modal-tab-panel">
      <StyleFieldGroup className="style-field-group--page-settings">
        <Tooltip title="Outer padding around the whole page in the reader">
          <Space size={6}>
            {SectionLabel.pagePadding}
            <Form.Item name="pagePaddingPx" style={{ marginBottom: 0 }}>
              <InputNumber
                size="small"
                min={0}
                max={64}
                step={1}
                style={{ width: 72 }}
                aria-label="Page padding"
              />
            </Form.Item>
          </Space>
        </Tooltip>
        <Tooltip title="Gap between each line of text in the reader">
          <Space size={6}>
            {SectionLabel.spacing}
            <Form.Item name="contentLineGapPx" style={{ marginBottom: 0 }}>
              <InputNumber size="small" min={0} max={48} step={1} style={{ width: 72 }} aria-label="Line gap" />
            </Form.Item>
          </Space>
        </Tooltip>
        <Tooltip title="Align all content left, center, right, or justified">
          <Space size={6}>
            {SectionLabel.layout}
            <Form.Item name="contentAlign" style={{ marginBottom: 0 }}>
              <Select
                size="small"
                style={{ width: 108 }}
                aria-label="Content align"
                options={[
                  { label: 'Left', value: 'left' },
                  { label: 'Center', value: 'center' },
                  { label: 'Right', value: 'right' },
                  { label: 'Justify', value: 'justify' },
                ]}
              />
            </Form.Item>
          </Space>
        </Tooltip>
      </StyleFieldGroup>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 12 }}>
        Page padding, line gap, and alignment are reflected in the preview below.
      </Typography.Paragraph>
    </div>
  );

  const previewPagePadding = Math.max(0, Number(pagePaddingPx) || 0);

  return (
    <div>
      <Tabs
        className="style-modal-tabs"
        size="small"
        type="card"
        activeKey={activeStyleTab}
        onChange={setActiveStyleTab}
        destroyOnHidden={false}
        items={[
          { key: 'page', label: SectionLabel.page, children: pageTab },
          {
            key: 'headerAdvanced',
            label: SectionLabel.header,
            children: (
              <LineStyleEditTabs
                form={form}
                namePrefix="defaultHeaderStyle"
                mode="header"
                colors={{
                  color: headerTextColor,
                  backgroundColor: headerBgColor,
                  borderColor: headerBorderColor,
                }}
              />
            ),
          },
          {
            key: 'lineAdvanced',
            label: SectionLabel.line,
            children: (
              <LineStyleEditTabs
                form={form}
                namePrefix="defaultLineStyle"
                showSpan
                colors={{
                  color: lineTextColor,
                  backgroundColor: lineBgColor,
                  borderColor: lineBorderColor,
                }}
              />
            ),
          },
          {
            key: 'nestedLineAdvanced',
            label: SectionLabel.nestedLine,
            children: (
              <LineStyleEditTabs
                form={form}
                namePrefix="defaultNestedLineStyle"
                colors={{
                  color: nestedLineTextColor,
                  backgroundColor: nestedLineBgColor,
                  borderColor: nestedLineBorderColor,
                }}
              />
            ),
          },
          {
            key: 'deepNestedLineAdvanced',
            label: SectionLabel.deepNestedLine,
            children: (
              <LineStyleEditTabs
                form={form}
                namePrefix="defaultDeepNestedLineStyle"
                colors={{
                  color: deepNestedLineTextColor,
                  backgroundColor: deepNestedLineBgColor,
                  borderColor: deepNestedLineBorderColor,
                }}
              />
            ),
          },
          {
            key: 'linkAdvanced',
            label: SectionLabel.links,
            children: (
              <LineStyleEditTabs
                form={form}
                namePrefix="defaultLinkStyle"
                colors={{
                  color: linkTextColor,
                  backgroundColor: linkBgColor,
                  borderColor: linkBorderColor,
                }}
              />
            ),
          },
        ]}
      />

      <div style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <EyeOutlined style={{ fontSize: 14, color: '#605e5c' }} aria-hidden />
          <Typography.Text strong>Preview</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            — {previewTitle || 'how book content will look'}
          </Typography.Text>
        </div>
        <Card
          size="small"
          styles={{
            body: {
              padding: '10px 12px',
              ...(typeof defaultBackgroundColor === 'string' && defaultBackgroundColor.trim()
                ? { backgroundColor: defaultBackgroundColor.trim() }
                : {}),
            },
          }}
        >
          <div
            style={{
              padding: previewPagePadding,
              background: previewPagePadding > 0 ? 'rgba(0, 0, 0, 0.03)' : undefined,
              borderRadius: previewPagePadding > 0 ? 6 : undefined,
              ...(activeStyleTab === 'page' && previewPagePadding > 0
                ? { outline: '1px dashed #91caff', outlineOffset: -1 }
                : {}),
            }}
          >
            <div style={previewFlowStyle}>
            <div style={{ width: '100%', ...previewRowStyle(activeStyleTab === 'headerAdvanced') }}>
              <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                Header / section
              </Typography.Text>
              <span style={asPreviewCss(headerStyle)}>विधान शीर्षक</span>
            </div>

            <div style={{ width: '100%', ...previewRowStyle(activeStyleTab === 'lineAdvanced') }}>
              <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                Line
              </Typography.Text>
              <div style={buildLinePreviewCss(lineStyle, defaultTextColor)}>
                अथातः कर्म प्रारभ्यते। सम्पूर्णं कर्मानुष्ठानं कर्तव्यम्।
              </div>
            </div>

            <div style={{ width: '100%', ...previewRowStyle(activeStyleTab === 'nestedLineAdvanced') }}>
              <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                Nested line (level 1)
              </Typography.Text>
              <div style={buildLinePreviewCss(nestedLineStyle, defaultTextColor)}>
                उपविधि — सम्पूर्णं कर्मानुष्ठानम्।
              </div>
            </div>

            <div
              style={{
                width: '100%',
                ...previewRowStyle(activeStyleTab === 'deepNestedLineAdvanced'),
                marginLeft: contentAlign === 'center' || contentAlign === 'right' ? 0 : 20,
              }}
            >
              <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                Nested line (level 2+)
              </Typography.Text>
              <div style={buildLinePreviewCss(deepNestedLineStyle, defaultTextColor)}>
                तदनन्तरं ग्रहणं कर्तव्यम्।
              </div>
            </div>

            <div style={{ width: '100%', ...previewRowStyle(activeStyleTab === 'linkAdvanced') }}>
              <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                Link
              </Typography.Text>
              <span style={buildLinePreviewCss(lineStyle, defaultTextColor)}>
                सम्बद्ध विधि{' '}
                <span style={asPreviewCss(linkStyle)}>अन्य ग्रन्थ</span>
              </span>
            </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
